import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useContent } from "@/context/ContentContext";

// Soft three-note chime — only for visitors who have enabled the soundscape.
// Browsers may hold the AudioContext until the first tap; we fail silently then.
const playIntroChime = () => {
  try {
    if (!localStorage.getItem("rs_sound_pref")) return;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctx.resume().catch(() => {});
    const t0 = ctx.currentTime + 0.4;
    const master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);
    master.gain.setTargetAtTime(0.16, t0, 0.2);
    [
      [659.25, 0],
      [880, 0.2],
      [1318.5, 0.42],
    ].forEach(([f, d]) => {
      const o = ctx.createOscillator();
      o.type = "sine";
      o.frequency.value = f;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0 + d);
      g.gain.exponentialRampToValueAtTime(0.5, t0 + d + 0.04);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + d + 1.7);
      o.connect(g).connect(master);
      o.start(t0 + d);
      o.stop(t0 + d + 1.9);
    });
    setTimeout(() => ctx.close().catch(() => {}), 3200);
  } catch {}
};

const MaskLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

// One-time-per-session brand curtain: masked line-by-line reveal, gold sweep, lift.
export const BrandIntro = () => {
  const { content } = useContent();
  const [show, setShow] = useState(() => {
    try {
      return !sessionStorage.getItem("rs_intro_seen");
    } catch {
      return false;
    }
  });
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    if (content?.settings?.sound_enabled !== "off") playIntroChime();
    document.body.style.overflow = "hidden";
    const t1 = setTimeout(() => setLeaving(true), 2300);
    const t2 = setTimeout(() => dismiss(), 3100);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const dismiss = () => {
    try {
      sessionStorage.setItem("rs_intro_seen", "1");
    } catch {}
    document.body.style.overflow = "";
    setShow(false);
  };

  if (!show) return null;

  const name = content?.settings?.clinic_name || "Royal Skin Cosmetic Clinic";
  const brand = name.replace(/\s*cosmetic\s*clinic\s*/i, "").trim() || "Royal Skin";

  return (
    <motion.div
      data-testid="brand-intro"
      role="presentation"
      onClick={dismiss}
      className="fixed inset-0 z-[100] flex cursor-pointer flex-col items-center justify-center bg-[#0C0A09]"
      initial={false}
      animate={{ y: leaving ? "-100%" : 0 }}
      transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
    >
      {content?.settings?.logo_url && (
        <motion.img
          src={content.settings.logo_url}
          alt=""
          className="mb-7 h-20 w-auto object-contain drop-shadow-[0_8px_30px_rgba(212,175,55,0.25)] sm:h-24"
          initial={{ opacity: 0, scale: 0.82, y: 14 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
          data-testid="brand-intro-logo"
        />
      )}
      <MaskLine delay={0.15} className="text-[10px] font-medium uppercase tracking-[0.55em] text-[#D4AF37]">
        Welcome to
      </MaskLine>
      <h1 className="mt-5 text-center font-display text-[clamp(3rem,10vw,7.5rem)] font-normal leading-[0.95] tracking-tight text-[#F5EFE6]">
        <MaskLine delay={0.35}>{brand}</MaskLine>
      </h1>
      <motion.div
        aria-hidden="true"
        className="mt-7 h-px w-48 origin-center bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent sm:w-72"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.9 }}
      />
      <MaskLine delay={1.1} className="mt-6 text-[10px] font-medium uppercase tracking-[0.5em] text-[#A39288]">
        Cosmetic Clinic · Dubai
      </MaskLine>
      <motion.p
        className="absolute bottom-8 text-[9px] uppercase tracking-[0.3em] text-[#A39288]/60"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
      >
        Tap to enter
      </motion.p>
    </motion.div>
  );
};
